import _ from 'lodash';
import Icons from './customMarkerIcons';
import template from './equipmentMapPanel.html';
import markerControlsTemplate from './MarkerControlsTemplate';
import { segmentTrips } from './tripSegmentation';


export default {
  name: 'equipmentMap',
  template,
  controllerAs: 'EquipMapCtrl',
  controller: EquipmentMapController,
  bindings: {
    panelId: '<'
  }
};

/* @ngInject */
function EquipmentMapController(
  $scope,
  $compile,
  $element,
  EquipmentInfoService,
  EventInfoService,
  EventsPanelService,
  DashboardService,
  MapService,
  CustomMarkerService,
  EventMarkerService,
  ObserverService,
  MatchMapStyles,
) {
  const vm = this;

  /**
   * Private Variables
   */
  let googleEquipmentMap;
  let customMarkerFactory;
  let eventRequests = 0;
  let eventResponse;
  let pendingMarkers = [];
  let thePopup;
  let setZeroPopupOpacity;
  let pinpointMarker;
  let pinpointTimeout;
  let previousTargetMarker;
  let markerControlScope;
  let showLatLong;
  let moveToNewEvent;
  let newEventZoom;
  let pinpointZoom;
  let oms;

  const markers = [];
  const infoBoxes = [];
  // emergencyGps was intentionally removed here (and from LocationEventConstant):
  // the SPOT/emergencyGps tracker type has no remaining devices and is being
  // retired, so it no longer needs the location-event retrieval path.
  const equipsWithLocationUpdates = ['gps', 'padlocks', 'earthquakes'];
  // Equipment types whose location event the API excludes from its default
  // event-name set (config/events.js eventPanelExclusions), so it has to be
  // asked for by name. Only `gps` is affected -- padlocks/earthquakes events
  // are not excluded, so those queries are left untouched.
  const EXCLUDED_LOCATION_EVENTS = { gps: ['gps/regularUpdate'] };
  const markerClusters = {};
  const USA = {
    lat: 32,
    lng: -97
  };
  const gpsTrack = 'gps/track';
  const gpsTrackEv = 'gps/trackEvents';
  const gpsTrackMark = 'gps/trackMarkers';
  // Declutter the base map: hide POIs (parks/forests), land-parcel and
  // neighbourhood boundaries, and natural-landscape labels -- these are Google's
  // own base-map features (incl. the shaded named regions) that add noise to an
  // equipment map. Roads and place (city) labels are kept. Appended AFTER the
  // shared dark theme (MatchMapStyles) so it keeps that look and only adds these
  // hides -- passing styles replaces MapService.Map's default entirely.
  const MAP_DECLUTTER_STYLES = [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
    { featureType: 'landscape.natural', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ];
  const mapOptions = {
    center: new google.maps.LatLng(USA.lat, USA.lng),
    zoom: 16,
    minZoom: 3,
    styles: [...MatchMapStyles, ...MAP_DECLUTTER_STYLES],
  };
  // Teardrop pin as a two-path SVG (white outer + a scaled-down colored inner)
  // so the color sits inside a white border, like the original raster pin. A
  // single google.maps.Symbol can't nest two shapes, so this is a data-URI icon.
  // viewBox 24x32, tip at (12, 30); rendered 30x40 => tip at px (15, 38).
  const TEARDROP_D = 'M12 1C6.5 1 2 5.5 2 11c0 7 10 19 10 19s10-12 10-19C22 5.5 17.5 1 12 1Z';
  const TEARDROP_INNER_SCALE = 0.74;
  const DEFAULT_PIN_COLOR = '#2b2e33';
  const withdrawFromColorBank = colorBank();
  const LIMIT_DEFAULT = 25;
  const FILTERDATE_DEFAULT = Date.now();
  // History window presets. Selecting one re-queries location events over
  // `Date.now() - ms` .. now instead of the most-recent-N default. A generous
  // limit still caps the fetch so a chatty tracker cannot flood the browser;
  // the API itself clamps any range to 90 days.
  const HOURS_PER_DAY = 24;
  const MS_PER_MINUTE = 60 * 1000;
  const MS_PER_HOUR = 60 * MS_PER_MINUTE;
  const MS_PER_DAY = HOURS_PER_DAY * MS_PER_HOUR;
  const WINDOW_LIMIT_CAP = 2000;
  const HISTORY_WINDOWS = [
    { label: 'Last 25 fixes', ms: null },
    { label: 'Last 24 hours', ms: MS_PER_DAY },
    { label: 'Last 3 days', ms: 3 * MS_PER_DAY },
    { label: 'Last 7 days', ms: 7 * MS_PER_DAY },
    { label: 'Last 30 days', ms: 30 * MS_PER_DAY },
  ];
  // Google base map styles. `value` matches google.maps.MapTypeId keys
  // (uppercased) and the `mapType` panel-config value.
  const DEFAULT_MAP_STYLE = 'roadmap';
  const MAP_STYLES = [
    { label: 'Road', value: 'roadmap' },
    { label: 'Satellite', value: 'satellite' },
    { label: 'Hybrid', value: 'hybrid' },
    { label: 'Terrain', value: 'terrain' },
  ];

  const DEFAULT_PINPOINT_ZOOM = 19;

  const animationLength = {
    start: 1000,
    stop: 3000,
    pop: 4000
  };
  const tileLoadEventTimeout = 5000;
  const eventRetrievalTimer = 10000;
  const overNineThousand = 9001;
  const pinpointLat = -0.2;
  const pinpointLng = -10;
  const debounceZoom = 500;
  const COPY_FEEDBACK_MS = 1500;
  const symEquipment = Symbol.for('equipment');

  /**
   * Public Variables
   */
  vm.panel = null;
  vm.loadingSpinners = {};
  vm.editPanel = {
    needsToBeConfigured: false
  };
  vm.headerModel = {
    all: {
      id: 'all',
      name: 'All Equipment'
    }
  };
  vm.movedEquip = null;
  vm.equipmentStates = {
    all: EventMarkerService.initializeStore(['Markers'], {
      allMarkers: [],
      baseMarkers: [],
      polyLine: []
    })
  };
  vm.activeEquipmentId = 'all';
  vm.markerCheckboxes = EventMarkerService.initializeCheckboxes();
  // GPS history controls. `historyView` picks how a trail is drawn:
  //   'trips' -> stop/trip timeline (a marker per dwell, lines for moves)
  //   'raw'   -> every fix as a numbered breadcrumb (the original behaviour)
  // `historyWindowMs` is the query window; null means the most-recent-N default.
  vm.historyWindows = HISTORY_WINDOWS;
  vm.historyWindowMs = null;
  vm.historyView = 'trips';
  vm.hasLocationEquipment = false;
  vm.mapStyles = MAP_STYLES;
  vm.mapStyle = DEFAULT_MAP_STYLE;

  /**
   * Public Methods
   */
  vm.$onInit = init;
  vm.$postLink = postLink;
  vm.$onDestroy = destroy;
  vm.fitZoomMap = _.debounce(fitZoomMap, debounceZoom);
  vm.useEventsToChangePosition = useEventsToChangePosition;
  vm.close = close;
  vm.closeNotificationPopup = closeNotificationPopup;
  vm.toggleCheckbox = toggleCheckbox;
  vm.copyToClipboard = copyToClipboard;
  vm.setHistoryWindow = setHistoryWindow;
  vm.setHistoryView = setHistoryView;
  vm.setMapStyle = setMapStyle;

  function init() {
    vm.currentDashboard = DashboardService.getCurrentDashboard();
    vm.panel = vm.currentDashboard.panels[vm.panelId];

    ({
      showLatLong = true,
      moveToNewEvent = true,
      newEventZoom,
      pinpointZoom = pinpointZoom
    } = vm.panel.config);

    // Panel-configured GPS history defaults (set in the panel editor): the
    // initial view ('trips' | 'raw') and the initial window (ms, or null for the
    // most-recent-N default). Fall back to the built-in defaults when unset.
    if (vm.panel.config.historyView) {
      vm.historyView = vm.panel.config.historyView;
    }
    if (!_.isUndefined(vm.panel.config.historyWindowMs)) {
      // Normalise to a number or null so it matches the window control's options
      // (the editor may store it as a string). Empty/0 means the most-recent-N
      // default.
      vm.historyWindowMs = vm.panel.config.historyWindowMs ? Number(vm.panel.config.historyWindowMs) : null;
    }
    if (vm.panel.config.mapType) {
      vm.mapStyle = vm.panel.config.mapType;
    }

    customMarkerFactory = new CustomMarkerService.CustomMarkerFactory(EventMarkerService.getConfig(), {
      markerLabel: 'Event',
      labelClass: 'default-event-label',
      markerData: Icons.gray
    });

    if (vm.panel.equipment && vm.panel.equipment.length) {
      vm.loadingSpinners.stopEquipMapPanel = false;
      vm.editPanel.needsToBeConfigured = false;

      EquipmentInfoService.getFullEquipment(vm.panel.equipment)
        .then((equipmentArray) => {
          const waitForMapDiv = $scope.$watch(() => document.querySelector(`#equipment-map-${vm.panel.id}`),
            (newValue) => {
              if (newValue) {
                waitForMapDiv();
                thePopup = $element.find('.notification-popup');
                handleNotificationPopup({ description: 'Retrieving all events...' });
                setTimeout(() => {
                  closeNotificationPopup();
                  $scope.$digest();
                }, eventRetrievalTimer);
                createEquipmentMap(newValue, _.uniq(_.map(equipmentArray, 'equipmentType')));

                if (equipmentArray.length === 1) {
                  vm.activeEquipmentId = equipmentArray[0].id;
                  delete vm.headerModel.all;
                }
                _.forEach(equipmentArray, (equipment) => {
                  equipment = angular.copy(equipment);
                  vm.headerModel[equipment.id] = equipment;

                  vm.equipmentStates[equipment.id] =
              EventMarkerService.initializeStore(['Events', 'Markers'], {
                equipment,
                events: [],
                allMarkers: [],
                baseMarkers: [],
                polyLine: null,
                strokeColor: withdrawFromColorBank()
              });

                  // If equipment has regular Location updates, save it in an array for later,
                  // as events for the equipment are retrieved later on
                  if (_.includes(equipsWithLocationUpdates, equipment.equipmentType)) {
                    eventRequests += 1;
                    vm.hasLocationEquipment = true;
                    vm.equipmentStates[equipment.id].regularUpdateEvents = [];
                    retrieveEventsForSingleEquipment(equipment.id, LIMIT_DEFAULT, FILTERDATE_DEFAULT);
                  } else {
                    // Otherwise, is a static equipment, use the location in the equipment settings
                    assigninfoboxAndCollectMarker(makeMarker(equipment), equipment);
                  }
                });
              }
            });
        });
    } else {
      // panel needs to be configured
      vm.loadingSpinners.stopEquipMapPanel = true;
      vm.editPanel.needsToBeConfigured = true;
    }
  }

  function postLink() {
    $scope.$on('panel-resized', () => {
      setTimeout(() => google.maps.event.trigger(googleEquipmentMap, 'resize'), 100);
    });
    $scope.$on('$stateChangeStart', close);
    setupEventListeners();
  }

  function retrieveEventsForSingleEquipment(equipment, limit = LIMIT_DEFAULT, filterDate = FILTERDATE_DEFAULT) {
    const equipmentState = vm.equipmentStates[equipment];
    limit = Number(_.get(equipmentState, 'equipment.settings.limit', limit));
    const eventNames = EXCLUDED_LOCATION_EVENTS[_.get(equipmentState, 'equipment.equipmentType')];

    const query = {
      equipment,
      limit
    };
    // A selected history window queries a time range (startDate .. now) instead of
    // the most-recent-N default, raising the limit to a safety cap so a long
    // window on a chatty tracker still cannot flood the browser. The API clamps
    // any range to 90 days. filterDate is NOT sent with a window: the API's
    // getDateRange lets filterDate override startDate, so sending both silently
    // ignored the window and returned the most-recent set within 90 days.
    if (vm.historyWindowMs) {
      query.startDate = Date.now() - vm.historyWindowMs;
      query.endDate = Date.now();
      query.limit = Math.max(limit, WINDOW_LIMIT_CAP);
    } else {
      query.filterDate = filterDate;
    }
    // Without naming them, the API falls back to eventPanelEvents, which is every
    // event name MINUS eventPanelExclusions -- and gps/regularUpdate is excluded.
    // gps equipment therefore received no location events here at all, so its
    // trail was empty on load and only ever built up from live socket events.
    // Naming the event explicitly bypasses the exclusion.
    if (eventNames) {
      query.eventNames = eventNames;
    }

    // call for events here, then update the marker's latlng from most recent
    EventInfoService
      .getEvents(query)
      .then(events => _.reject(events, ({ eventName }) => eventName.includes('/acknowledgement')))
      .then((events) => {
        // Save all events
        _.forEach(events, (event) => {
          // unshift here and in sortEvents so that newest are at the back of array, allowing to push new ones
          normaliseTrackEventName(event);
          vm.equipmentStates[event.equipment.id].events.unshift(event);
          sortEvent(event.equipment.id, event, true);
        });

        const marker = createMainEquipmentMarker(equipmentState);

        addAllOtherEventMarkers(equipment);
        showBreadcrumbInternal(equipment);
        assigninfoboxAndCollectMarker(marker, equipmentState.equipment);

        // Put the freshly-drawn stops into this equipment's cluster. On the very
        // first load the cluster does not exist yet (created on tilesloaded, which
        // repaints then); on a later re-query (a history-window change) this is
        // what re-clusters the new stops, since the initial repaint has passed.
        repaintTheClusters(equipment, vm.markerCheckboxes[gpsTrack]);

        // TODO: debounce so this isn't called multiple times
        vm.fitZoomMap();
        eventResponse = eventResponse ? eventResponse += 1 : 1;
      })
      .catch((err) => {
        console.error(`Error getting events in Equipment Map panel: ${vm.panelId}`, err);
      });
  }

  function destroy() {
    ObserverService.unsubscribe('pinpoint', vm.panelId);
    MapService.removeIntervalsAndCloseInfoWins(infoBoxes);
    MapService.destroyGoogleMaps(`#equipment-map-${vm.panel.id}`);
    _.forEach(markerClusters, cluster => cluster.removeMarkers(cluster.getMarkers()));
    oms.clearMarkers();
    oms.clearListeners('click');
    MapService.unsetMarkers([...markers, ..._.map(markerClusters), ...vm.equipmentStates.all.polyLine]);
    googleEquipmentMap = null;
    markerControlScope.$destroy();
    _.forEach(markers, (marker) => {
      if (marker.getMap()) {
        marker.setMap(null);
      }
    });
  }

  /*
     * Private methods
     */
  function createMainEquipmentMarker(state) {
    let marker;
    // get last marker and show it
    if (state[gpsTrackEv].length) {
      const firstEvent = _.last(state[gpsTrackEv]);
      marker = makeMarker(
        state.equipment,
        EventsPanelService.getLat(firstEvent),
        EventsPanelService.getLong(firstEvent),
        firstEvent.eventDate,
        firstEvent.id
      );
      drawTrail(state);
    } else if (_.get(state, 'equipment.equipmentType') === 'padlocks' && state.events.length) {
      // get the first that matches unlockedLock or lockedLock events
      const firstEvent = _.findLast(state.events, { eventName: 'lockedLock' });
      if (firstEvent && EventsPanelService.eventHasCoordinates(firstEvent)) {
        marker = new CustomMarker(firstEvent, state.equipment);
        marker.id = state.equipment.id;
      }

    }

    marker = marker || makeMarker(state.equipment);
    marker[symEquipment] = state.equipment;
    return marker;
  }

  function handleEquipmentEvent(__, event, isOld) {
    if (EventsPanelService.eventHasCoordinates(event) === false || event.eventName.includes('/acknowledgement')) {
      return;
    }

    clearTimeout(setZeroPopupOpacity);

    const equipment = event.equipment;
    const state = vm.equipmentStates[equipment.id];

    if (!isOld) {
      handleNotificationPopup(event);
    }
    normaliseTrackEventName(event);

    // Add event to the beginning of the array
    state.events.push(event);
    const insertedIndex = sortEvent(equipment.id, event);

    // Note: we intentionally leave any open infobox for this equipment as-is.
    // The infobox is anchored to its marker, so it follows the marker to the new
    // position; force-closing it here made an open popup vanish on every update.
    if (eventIsATrack(event)) {
      const marker = _.find(markers, { id: equipment.id });
      if (marker) {
        handleTrackEvent(marker, event, isOld, insertedIndex);
      }
    } else if (isLockEvent(event)) {
      const marker = _.find(markers, { id: equipment.id });
      if (marker) {
        handlePadlockEvent(marker, event, isOld);
      }
    } else {
      handleAllOtherEvents(equipment.id, event);
    }
    setZeroPopupOpacity = setTimeout(closeNotificationPopup, animationLength.pop);
  }

  function handleTrackEvent(marker, event, notNewEvent, trackNumber) {
    const equipId = event.equipment.id;
    const state = vm.equipmentStates[equipId];
    const lat = EventsPanelService.getLat(event);
    const lng = EventsPanelService.getLong(event);
    const position = new google.maps.LatLng(lat, lng);
    let newBreadcrumb;
    let newMarkerDate;

    if (notNewEvent) {
      newBreadcrumb = addBreadcrumbMarker(
        event,
        null,
        null,
        '',
        event.eventDate
      );
    } else {
      const trackLength = state[gpsTrackEv].length;
      marker.setAnimation(null);
      const prevEventInd = trackNumber || trackLength - 2;
      const previousEvent = state[gpsTrackEv][prevEventInd];
      const latLongPreviousEvent = previousEvent ? {
        lat: EventsPanelService.getLat(previousEvent),
        lng: EventsPanelService.getLong(previousEvent)
      } : {
        lat: state.equipment.settings.latitude,
        lng: state.equipment.settings.longitude
      };
      if (trackNumber) {
        latLongPreviousEvent.lat = lat;
        latLongPreviousEvent.lng = lng;
        newMarkerDate = event.eventDate;

        updateLabels(trackNumber, state[gpsTrackMark]);
      } else {
        marker.setPosition(position);
        marker.eventDate = event.eventDate;
        marker.eventId = event.id;
        trackNumber = trackLength ? `${trackLength - 1}` : '0';
        newMarkerDate = previousEvent ? previousEvent.eventDate : state.equipment.eventDate;
      }
      addToBreadcrumbPolyLine(equipId, { lat, lng }, trackNumber);
      if (previousEvent) {
        newBreadcrumb = addBreadcrumbMarker(
          previousEvent || event,
          latLongPreviousEvent.lat,
          latLongPreviousEvent.lng,
          _.isString(trackNumber) ? trackNumber : trackNumber + 1,
          newMarkerDate
        );
      }
    }
    if (newBreadcrumb) {
      markerClusters[equipId].addMarker(newBreadcrumb);
      showBreadcrumbInternal(equipId);
      animateMarkersAndLines(marker);
      markerClusters[equipId].repaint();
    }

    if (moveToNewEvent && !notNewEvent) {
      changePosition({
        settings: {
          latitude: lat,
          longitude: lng
        }
      });
    }
    if (newEventZoom) {
      googleEquipmentMap.setZoom(Number(newEventZoom));
    }
  }

  function handlePadlockEvent(marker, event) {
    const lat = EventsPanelService.getLat(event);
    const lng = EventsPanelService.getLong(event);
    const position = new google.maps.LatLng(lat, lng);
    const eventName = event.eventName;
    const { markerData, markerLabel } = EventMarkerService.getMarkerConfig(eventName) || {};

    customMarkerFactory.setIcon(marker, markerData);
    marker.set('labelContent', markerLabel);
    marker.setPosition(position);
    marker.type = markerLabel;
    marker.eventDate = event.eventDate;

    if (newEventZoom) {
      googleEquipmentMap.setZoom(Number(newEventZoom));
    }
  }

  function handleAllOtherEvents(id, event) {
    addNonTrackEventMarker(id, event);
  }

  function handleNotificationPopup(event) {
    vm.notificationMessage = event.description;

    thePopup.css({
      opacity: 0.8,
      pointerEvents: 'all'
    });
  }

  function createEquipmentMap(element, equipmentTypes = []) {
    // vm.mapStyle carries the panel-configured (or default) base map style; the
    // runtime selector updates it via setMapStyle.
    mapOptions.mapTypeId = google.maps.MapTypeId[vm.mapStyle.toUpperCase()];

    googleEquipmentMap = new MapService.Map(element, vm.panel.id, mapOptions);

    oms = new MapService.MarkerSpiderfier(googleEquipmentMap, { markersWontMove: false });
    oms.addListener('click', (marker) => {
      showInfobox(marker.id);
    });
    markerControlScope = $scope.$new();
    const bodyToggle = ({ data }) => markerControlScope.toggleDropdown(data);
    markerControlScope.toggleDropdown = ({ currentTarget }) => {
      const targetClass = 'showDropdown';
      const { classList } = currentTarget;
      if (classList.contains(targetClass)) {
        classList.remove(targetClass);
        $('body').off('click', bodyToggle);
      } else {
        classList.add(targetClass);
        $('body').on('click', { currentTarget }, bodyToggle);
      }
    };
    markerControlScope.markerCheckboxes = vm.markerCheckboxes;
    markerControlScope.toggleCheckbox = vm.toggleCheckbox;
    const mapMarkerControls = $compile(markerControlsTemplate(EventMarkerService, equipmentTypes));

    mapMarkerControls(markerControlScope, (clonedElement) => {
      googleEquipmentMap.controls[google.maps.ControlPosition.TOP_LEFT].clear();
      googleEquipmentMap.controls[google.maps.ControlPosition.TOP_LEFT].push(clonedElement[0]);
    });

    const resizeAnimationLength = 300;
    MapService.safeResize(googleEquipmentMap, resizeAnimationLength);
    // wait for the map to load
    MapService.waitForTilesLoaded(googleEquipmentMap, () => {
      // One cluster per real equipment. No 'all' cluster: it duplicated every
      // equipment's markers, so a stop ended up in two clusterers -> doubled
      // "N" badges. When 'all' is the active view, each equipment's own cluster
      // is simply left visible.
      _.forEach(vm.equipmentStates, (__, id) => {
        if (id !== 'all') {
          markerClusters[id] = new MapService.MarkerCluster(googleEquipmentMap);
        }
      });
      _.forEach(pendingMarkers, (marker) => {
        marker.setMap(googleEquipmentMap);
      });
      pendingMarkers = null;
      if (markers.length) {
        vm.fitZoomMap();
      }
      vm.loadingSpinners.stopEquipMapPanel = true;
      $scope.$digest();

      let waitingTimedOut = false;
      const waitForAllEvents = $scope.$watch(() => eventRequests === eventResponse, (newValue) => {
        if (newValue || waitingTimedOut) {
          waitForAllEvents();
          closeNotificationPopup();
          _.forEach(markerClusters, (__, id) => {
            repaintTheClusters(id, true);
          });
        }
      });
      setTimeout(() => {
        waitingTimedOut = true;
        $scope.$digest();
      }, tileLoadEventTimeout);
    }, resizeAnimationLength);
  }

  // Builds a data-URI teardrop icon: a white teardrop with a smaller colored
  // teardrop centred inside it, giving a white border around the color. Anchor
  // is the tip.
  function teardropIcon(color) {
    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 24 32">' +
      `<path d="${TEARDROP_D}" fill="#ffffff"/>` +
      `<g transform="translate(12 12) scale(${TEARDROP_INNER_SCALE}) translate(-12 -12)">` +
      `<path d="${TEARDROP_D}" fill="${color}"/></g>` +
      '</svg>';
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      origin: { lat: 0, lng: 0 },
      anchor: { lat: 15, lng: 38 },
    };
  }

  function makeMarker(equipment, latitude, longitude, eventDate, eventId) {

    const { settings, equipmentType } = equipment;

    if (equipmentType === 'tripwires') {
      return null;
    }

    // Colored teardrop: the equipment's own color (settings.color), else the
    // color assigned to its trail, so a unit's pin matches its trail.
    const pinColor = _.get(settings, 'color') ||
      _.get(vm.equipmentStates, [equipment.id, 'strokeColor']) || DEFAULT_PIN_COLOR;
    const icon = teardropIcon(pinColor);

    const position = longitude && latitude ?
      new google.maps.LatLng(latitude, longitude) :
      new google.maps.LatLng(settings.latitude, settings.longitude);
    const newMarker =
        new MapService.Marker(googleEquipmentMap, equipment, icon, true, { position, eventDate, eventId });

    /* Try once more to set latest location, if not provided by events.
          This will move the existing marker if a recent location update exists.
          (I implemented the equipment location finding this way becase the existing
          event location code was very difficult to change in order to delay drawing) */
    if (!(latitude && longitude)) {
      EquipmentInfoService
        .getLocation(equipment.id)
        .then(({ lat, lng }) => {
          newMarker.setPosition(new google.maps.LatLng(lat, lng));
        })
        .catch(_.noop); // No location was ever recorded. No big deal.
    }

    oms.addMarker(newMarker);
    return newMarker;
  }

  function initializePolyline(id, lat = USA.lat, long = USA.lng) {
    vm.equipmentStates[id].polyLine = new MapService.Polyline(false, [new google.maps.LatLng(lat, long)]);
    // keep list of all polylines for easy manipulation later
    vm.equipmentStates.all.polyLine.push(vm.equipmentStates[id].polyLine);
    vm.equipmentStates[id].polyLine.setOptions({
      strokeColor: vm.equipmentStates[id].equipment.settings.color || vm.equipmentStates[id].strokeColor
    });
  }

  function animateMarkersAndLines(marker, line) {
    setTimeout(() => marker.setAnimation(google.maps.Animation.BOUNCE), animationLength.start);
    setTimeout(() => {
      marker.setAnimation(null);
      if (line) {
        line.setMap(null);
      }
    }, animationLength.stop);
    setZeroPopupOpacity = setTimeout(closeNotificationPopup, animationLength.pop);
  }

  function assigninfoboxAndCollectMarker(marker, equipment, id) {
    const { id: equipId } = equipment;

    equipmentInfoBox(id || equipId, marker);

    marker[symEquipment] = equipment;
    markers.push(marker);
    if (!googleEquipmentMap) {
      pendingMarkers.push(marker);
    }

    // A base marker is an equipment's own location pin (no event id passed). It
    // has no event array, so nothing tied it to a marker checkbox -- unchecking
    // e.g. Earthquake could not hide an earthquake feed's pin. Tag it with the
    // checkbox for its equipment type and register it so the visibility toggle
    // reaches it (kept out of the breadcrumb arrays, so clusters are untouched).
    if (_.isUndefined(id)) {
      if (!marker.eventName) {
        marker.eventName = baseMarkerCheckboxKey(equipment.equipmentType);
      }
      const state = vm.equipmentStates[equipId];
      if (state && state.baseMarkers) {
        state.baseMarkers.push(marker);
      }
      vm.equipmentStates.all.baseMarkers.push(marker);
    }
  }

  // The marker checkbox key that governs an equipment type's base pin -- the
  // first configured event whose prefix is that type (e.g. gps -> 'gps/track',
  // earthquakes -> 'earthquakes/earthquake'). Undefined for types with no
  // checkbox (static equipment), which then ignore the type toggles.
  function baseMarkerCheckboxKey(equipmentType) {
    return _.find(_.keys(vm.markerCheckboxes), key => key.split('/')[0] === equipmentType);
  }

  function equipmentInfoBox(id) {
    const verticalOffset = -50;
    infoBoxes.push(new MapService.Infobox(id, {
      boxStyle: {
        // No maxHeight: the panel-body clips overflow, so a fixed cap cut off the
        // last line (coordinates). Let the box size to its content instead.
        maxWidth: '280px'
      }
    }, verticalOffset));
  }

  function showInfobox(idEquipment) {
    const marker = _.find(markers, { id: idEquipment });
    marker.setAnimation(null);
    const position = marker.getPosition();
    const lat = position.lat();
    const lng = position.lng();
    const infobox = _.find(infoBoxes, { id: idEquipment });
    let formattedAddress = 'N/A';
    close();
    MapService.geoCode([lat, lng])
      .then(({ data }) => {
        formattedAddress = data.results.length > 0 ? data.results[0].formatted_address : formattedAddress;
      })
      .finally(() => {
        infobox.setContent(
          new MapService.GeocodeContentBox(marker, 'EquipMapCtrl', formattedAddress, lat, lng, showLatLong)
        );
        $compile(infobox.content_)($scope);
        infobox.open(googleEquipmentMap, marker);
        // workaround until google fixes their shit: https://issuetracker.google.com/issues/111997760
        setTimeout(() => {
          infobox.content_.parentNode.parentNode.parentNode.style.width = '100%';
        }, 100);
      });
  }

  function showBreadcrumbInternal(equipmentId) {
    const equipment = vm.equipmentStates[equipmentId];

    if (equipment.polyLine) {
      equipment.polyLine.setMap(googleEquipmentMap);
    } else {
      // make the call for the events and create the PolyLine
      const { settings } = vm.headerModel[equipmentId];
      initializePolyline(equipmentId, settings.latitude, settings.longitude);
    }
  }

  function addAllEventsToBreadcrumbPolyline(equipmentId, events) {
    _.forEach(events, (event, i) => {
      const lat = EventsPanelService.getLat(event, true);
      const lng = EventsPanelService.getLong(event, true);
      if (lat && lng) {
        addToBreadcrumbPolyLine(equipmentId, {
          lat,
          lng
        });
        if (i !== events.length - 1) {
          addBreadcrumbMarker(event, lat, lng, i + 1);
        }
      }
    });
  }

  // Draws a GPS trail in the currently selected view: the stop/trip timeline, or
  // the original numbered breadcrumb per fix.
  function drawTrail(state) {
    if (vm.historyView === 'trips') {
      addTripStopBreadcrumbs(state.equipment.id, state[gpsTrackEv]);
    } else {
      addAllEventsToBreadcrumbPolyline(state.equipment.id, state[gpsTrackEv]);
    }
  }

  // Switching between 'trips' and 'raw' only changes how the already-fetched
  // fixes are drawn, so it redraws in place without re-querying.
  function setHistoryView(view) {
    if (vm.historyView === view || !googleEquipmentMap) {
      return;
    }
    vm.historyView = view;
    eachLocationEquipment((state, id) => {
      clearTrail(id);
      if (state[gpsTrackEv] && state[gpsTrackEv].length) {
        drawTrail(state);
        showBreadcrumbInternal(id);
        repaintTheClusters(id, vm.markerCheckboxes[gpsTrack]);
      }
    });
    vm.fitZoomMap();
  }

  // Switches the base map style (road/satellite/hybrid/terrain) live.
  function setMapStyle(style) {
    vm.mapStyle = style;
    if (googleEquipmentMap) {
      googleEquipmentMap.setMapTypeId(google.maps.MapTypeId[style.toUpperCase()]);
    }
  }

  // Changing the window changes how much data to fetch, so it clears each trail
  // (trail markers, polyline and its main marker) and re-queries.
  function setHistoryWindow(ms) {
    if (!googleEquipmentMap) {
      return;
    }
    vm.historyWindowMs = ms;
    eachLocationEquipment((state, id) => {
      clearTrail(id);
      removeMainMarker(id);
      state.events = [];
      state[gpsTrackEv] = [];
      retrieveEventsForSingleEquipment(id);
    });
  }

  function eachLocationEquipment(fn) {
    _.forEach(vm.equipmentStates, (state, id) => {
      if (id !== 'all' && state.equipment &&
        _.includes(equipsWithLocationUpdates, state.equipment.equipmentType)) {
        fn(state, id);
      }
    });
  }

  // Removes a trail's breadcrumb markers and polyline (leaves the main marker and
  // the fetched events), so the trail can be redrawn cleanly.
  function clearTrail(id) {
    const state = vm.equipmentStates[id];
    const trail = state[gpsTrackMark] ? [...state[gpsTrackMark]] : [];
    if (markerClusters[id]) {
      markerClusters[id].removeMarkers(trail);
    }
    _.forEach(trail, (marker) => {
      if (marker.getMap && marker.getMap()) {
        marker.setMap(null);
      }
    });
    _.pullAll(markers, trail);
    _.pullAll(state.allMarkers, trail);
    _.pullAll(vm.equipmentStates.all.allMarkers, trail);
    _.pullAll(vm.equipmentStates.all[gpsTrackMark], trail);
    const trailIds = new Set(_.map(trail, 'id'));
    _.remove(infoBoxes, ({ id: boxId }) => trailIds.has(boxId));
    state[gpsTrackMark] = [];
    if (state.polyLine) {
      state.polyLine.setMap(null);
      _.pull(vm.equipmentStates.all.polyLine, state.polyLine);
      state.polyLine = null;
    }
  }

  function removeMainMarker(id) {
    const marker = _.find(markers, { id });
    if (!marker) {
      return;
    }
    if (marker.getMap && marker.getMap()) {
      marker.setMap(null);
    }
    if (oms && typeof oms.removeMarker === 'function') {
      oms.removeMarker(marker);
    }
    _.pull(markers, marker);
    _.pull(vm.equipmentStates.all.allMarkers, marker);
    _.pull(vm.equipmentStates.all.baseMarkers, marker);
    if (vm.equipmentStates[id]) {
      _.pull(vm.equipmentStates[id].baseMarkers, marker);
    }
    _.remove(infoBoxes, ({ id: boxId }) => boxId === id);
  }

  // Draws the trip/stop timeline for a GPS trail: the full path as one polyline
  // (so a trip's line connects its stops) plus a single labelled marker at each
  // dwell. A stationary asset's jitter cloud, or a multi-day history, collapses
  // to a handful of lettered stops instead of a pile of overlapping breadcrumbs.
  function addTripStopBreadcrumbs(equipmentId, events) {
    const fixes = _.map(events, event => ({
      lat: EventsPanelService.getLat(event, true),
      lng: EventsPanelService.getLong(event, true),
      date: event.eventDate,
      event
    }));
    const { segments, stops } = segmentTrips(fixes);

    // Build the trail line from segments: a stop contributes a single point (its
    // centre), not its whole jitter cloud, so a stationary asset or a wide window
    // sets a handful of polyline vertices instead of thousands -- and set them in
    // one call, not one setPath per fix (which redrew the line thousands of times
    // and froze the browser on a 30-day window).
    const linePoints = [];
    _.forEach(segments, (segment) => {
      if (segment.type === 'stop') {
        linePoints.push({ lat: segment.lat, lng: segment.lng });
      } else {
        _.forEach(segment.path, ({ lat, lng }) => linePoints.push({ lat, lng }));
      }
    });
    setBreadcrumbPath(equipmentId, linePoints);

    // A stop's representative event is the one at its arrival time; fall back to
    // the first event so the marker's infobox/click path always has an event.
    const eventByTime = new Map(_.map(fixes, ({ date, event }) => [new Date(date).getTime(), event]));
    _.forEach(stops, (stop, index) => {
      const stopEvent = eventByTime.get(stop.startDate.getTime()) || events[0];
      addBreadcrumbMarker(stopEvent, stop.lat, stop.lng, stopLabel(index), stop.startDate);
    });
  }

  // Replaces a trail's polyline path in a single setPath (one redraw), unlike
  // addToBreadcrumbPolyLine which pushes and redraws per point (fine for the one
  // live event at a time, ruinous for thousands at once).
  function setBreadcrumbPath(id, linePoints) {
    if (!linePoints.length) {
      return;
    }
    if (!vm.equipmentStates[id].polyLine) {
      initializePolyline(id, linePoints[0].lat, linePoints[0].lng);
    }
    vm.equipmentStates[id].polyLine.setPath(
      _.map(linePoints, ({ lat, lng }) => new google.maps.LatLng(lat, lng))
    );
  }

  // A, B, ... Z, AA, AB, ... for successive stops.
  function stopLabel(index) {
    const alphabet = 26;
    const letter = String.fromCodePoint('A'.codePointAt(0) + (index % alphabet));
    const cycle = Math.floor(index / alphabet);
    return cycle ? `${stopLabel(cycle - 1)}${letter}` : letter;
  }

  function addToBreadcrumbPolyLine(id, { lat, lng }, index) {
    if (vm.equipmentStates[id].polyLine) {
      const path = vm.equipmentStates[id].polyLine.getPath();
      if (index) {
        path.insertAt(index, new google.maps.LatLng(lat, lng));
      } else {
        path.push(new google.maps.LatLng(lat, lng));
      }
      vm.equipmentStates[id].polyLine.setPath(path);
    } else {
      initializePolyline(id, lat, lng);
    }
  }

  function addBreadcrumbMarker(event, lat, lng, label, eventDate) {
    const state = vm.equipmentStates[event.equipment.id || event.equipment];
    if (!lat || !lng) {
      lat = EventsPanelService.getLat(event);
      lng = EventsPanelService.getLong(event);
    }
    const date = eventDate || event.eventDate;
    const newBreadcrumb = new BreadcrumbMarker(state.equipment, lat, lng, event, label, date);
    newBreadcrumb[symEquipment] = state.equipment;
    equipmentInfoBox(newBreadcrumb.id, newBreadcrumb);
    // Make the breadcrumb node clickable: open its own infobox (that point's
    // date/coordinates/address), reusing the same path as the main marker.
    google.maps.event.addListener(newBreadcrumb, 'click', () => showInfobox(newBreadcrumb.id));
    // keep these references for easy manipulation later
    markers.push(newBreadcrumb);
    state[gpsTrackMark].push(newBreadcrumb);
    state.allMarkers.push(newBreadcrumb);
    vm.equipmentStates.all.allMarkers.push(newBreadcrumb);
    vm.equipmentStates.all[gpsTrackMark].push(newBreadcrumb);
    if (eventIsATrack(event) && event.eventName !== gpsTrack) {
      const eventName = event.eventName;
      const targetMarkers = state[`${eventName}Markers`];
      targetMarkers.push(newBreadcrumb);
      vm.equipmentStates.all[`${eventName}Markers`].push(newBreadcrumb);
    }

    return newBreadcrumb;
  }

  function BreadcrumbMarker(equipment, lat, lng, event, label, eventDate) {
    const position = new google.maps.LatLng(lat, lng);
    const symbolWithColor = angular.copy(MapService.breadcrumbSymbol);
    const labelContent = {
      text: label && label.toString(),
      fontSize: '10px',
      fontWeight: 'bold',
      // White with a dark halo (via the class) so the letter reads on any dot
      // color and any base map -- it was the default black, washing out on
      // light dots/imagery.
      color: '#ffffff',
      className: 'breadcrumb-stop-label'
    };
    symbolWithColor.fillColor =
      vm.equipmentStates[equipment.id].equipment.settings.color || vm.equipmentStates[equipment.id].strokeColor;
    // gpsTrackMark ('gps/trackMarkers') is a key of equipmentStates, not of
    // markerCheckboxes -- the checkboxes are keyed by event name -- so use
    // gpsTrack ('gps/track') here; the *Mark key was always undefined.
    const visible = (vm.activeEquipmentId === 'all' || equipment.id === vm.activeEquipmentId) &&
      vm.markerCheckboxes[gpsTrack];
    return new MapService.Marker(null, equipment, symbolWithColor, false, {
      position,
      id: event.id,
      label: label ? labelContent : null,
      type: EventMarkerService.getMarkerConfig(event.eventName)?.markerLabel || _.startCase(event.eventName),
      // `type` is a display label ('Track'), so it cannot be used to look up a
      // checkbox. Keep the event name alongside it for that.
      eventName: event.eventName,
      eventDate,
      visible,
      // Native hover tooltip. The date was already on the marker but only
      // reachable by clicking through to the infobox, which also costs a geocode
      // round-trip. `options` is assigned last in MapService.Marker, so this
      // overrides the default title of the equipment name.
      title: `${equipment.name} — ${MapService.formatEventDate(eventDate)}`
    });
  }

  function addAllOtherEventMarkers(equipmentId) {
    const state = vm.equipmentStates[equipmentId];
    _.forEach(state.events, (event) => {
      if (isNonLocationEvent(event)) {
        addNonTrackEventMarker(equipmentId, event, true);
      }
    });
  }

  function isNonLocationEvent(event) {
    return !eventIsATrack(event) && !isLockEvent(event) && EventsPanelService.eventHasCoordinates(event);
  }

  function isLockEvent(event) {
    return _.includes(event.eventName, 'lockedLock');
  }

  function addNonTrackEventMarker(equipmentId, event, unshift) {
    const state = vm.equipmentStates[equipmentId];
    const name = event.eventName;
    const newMarker = new CustomMarker(event, state.equipment);
    newMarker[symEquipment] = state.equipment;
    assigninfoboxAndCollectMarker(newMarker, state.equipment, event.id);
    storeMarker(newMarker, state, name, unshift);

    return newMarker;
  }


  function storeMarker(marker, state, name, unshift) {
    const markerName = `${name}Markers`;
    state[markerName] = state[markerName] || [];
    unshift ? state[markerName].unshift(marker) : state[markerName].push(marker);
    state.allMarkers.push(marker);
    vm.equipmentStates.all.allMarkers.push(marker);
    vm.equipmentStates.all[markerName] = vm.equipmentStates.all[markerName] || [];
    vm.equipmentStates.all[markerName].push(marker);
  }

  function CustomMarker(event, equipment) {
    // Use data.messagedetail when an event carries it; otherwise fall back to the
    // event description.
    const messagedetail = _.get(event, 'data.messagedetail', event.description);
    const eventName = event.eventName;
    const map = vm.markerCheckboxes[eventName] ? googleEquipmentMap : null;
    return customMarkerFactory.customMarker(
      map,
      eventName,
      EventsPanelService.getLat(event),
      EventsPanelService.getLong(event),
      {
        id: event.id,
        title: `${equipment.name} — ${MapService.formatEventDate(
          _.get(event, 'data.timestamp', event.eventDate)
        )}`,
        messagedetail,
        equipmentType: equipment.equipmentType,
        eventDate: _.get(event, 'data.timestamp', event.eventDate),
        type: EventMarkerService.getMarkerConfig(eventName)?.markerLabel || _.startCase(eventName),
        // `type` above is a display label; the checkbox lookup in
        // loopAndToggleMarkerVisibility needs the event name.
        eventName,
      }
    );
  }

  // Regular GPS updates are stored as gps/regularUpdate but render as a gps/track
  // breadcrumb trail. Canonicalise the name so both the live (handleNewEvent) and
  // initial-load paths bucket them as tracks -- eventIsATrack() only matches names
  // containing "TRACK", so an un-normalised gps/regularUpdate never enters the trail.
  function normaliseTrackEventName(event) {
    if (event.eventName === 'gps/regularUpdate') {
      event.eventName = gpsTrack;
    }
  }

  function sortEvent(id, event, unshift) {
    const state = vm.equipmentStates[id];
    let eventName = event.eventName;

    if (eventIsATrack(event) && eventName !== gpsTrack) {
      // A track-like event whose name is not in LocationEventConstant has no
      // pre-initialized bucket; create it defensively before pushing.
      state[`${eventName}Events`] = state[`${eventName}Events`] || [];
      state[`${eventName}Events`].push(event);
      eventName = gpsTrack;
    }
    const targetEventsName = `${eventName}Events`;
    state[targetEventsName] = state[targetEventsName] || [];
    const targetEvents = state[targetEventsName];
    let insertionIndex;

    if (unshift || !targetEvents.length) {
      targetEvents.unshift(event);
    } else {
      // If events are received out of order, splice them in the correct order
      _.forEachRight(targetEvents, (oldEvent, ind) => {
        if (new Date(event.eventDate) > new Date(oldEvent.eventDate)) {
          targetEvents.splice(ind + 1, 0, event);
          insertionIndex = ind + 1;
          return false;
        }
        return true;
      });
    }

    return insertionIndex + 1 === targetEvents.length ? null : insertionIndex;
  }

  function updateLabels(start, tracks) {
    for (let i = start; i < tracks.length; i += 1) {
      const newLabel = {
        text: `${Number(tracks[i].getLabel().text) + 1}`,
        fontSize: '10px'
      };
      tracks[i].setLabel(newLabel);
    }
  }

  function fitZoomMap(showBreadcrumbs, markerId) {
    const targetMarkers = markerId ? [_.find(markers, { id: markerId })] : markers;
    const bounds = new google.maps.LatLngBounds();
    targetMarkers.forEach((marker) => {
      bounds.extend(marker.getPosition());
      if (showBreadcrumbs) {
        // If we are showing breadcrumbs, show the whole path in the map
        _.forEach(vm.equipmentStates[marker.id].events, (event) => {
          bounds.extend(new google.maps.LatLng(EventsPanelService.getLat(event), EventsPanelService.getLong(event)));
        });
      }
    });
    googleEquipmentMap?.fitBounds(bounds);
  }

  function useEventsToChangePosition(equipment, fit = true) {
    const previousActiveEquipment = vm.activeEquipmentId;
    vm.activeEquipmentId = equipment.id;
    if (vm.activeEquipmentId === 'all') {
      turnAllEquipmentMarkersVisible(vm.activeEquipmentId, previousActiveEquipment);
      vm.fitZoomMap();
    } else {
      const events = vm.equipmentStates[equipment.id].events;

      turnTargetEquipmentMarkersVisible(vm.activeEquipmentId, previousActiveEquipment);

      if (fit) {
        if (events.length) {
          vm.fitZoomMap(!_.eq(equipment.equipmentType, 'padlocks'), equipment.id);
        } else {
          changePosition(equipment);
        }
      }
    }
  }

  function changePosition({ settings }) {
    MapService.changeCenter(googleEquipmentMap, settings);
  }

  function toggleCheckbox(box) {
    vm.markerCheckboxes[box] = !vm.markerCheckboxes[box];
    turnTargetEquipmentMarkersVisible(vm.activeEquipmentId, null, box);
  }

  function turnAllEquipmentMarkersVisible(newActive, oldActive) {
    if (newActive !== oldActive) {
      // turn oldActive's markers invisible
      // make newActive's markers visibility dependent upon markerCheckboxes boolean
      _.forEach(vm.equipmentStates, (__, id) => {
        loopAndToggleMarkerVisibility(id, 'all');
      });
    }
  }

  function turnTargetEquipmentMarkersVisible(newActive, oldActive, markerType) {
    if (oldActive && newActive !== oldActive) {
      // Focusing one equipment: hide EVERY other equipment (each has its own
      // marker cluster, so hiding only the previously-active one left other
      // equipment's stops/trails on the map), then show the focused one.
      _.forEach(vm.equipmentStates, (__, id) => {
        if (id !== newActive) {
          loopAndToggleMarkerVisibility(id, 'all', false);
        }
      });
      loopAndToggleMarkerVisibility(newActive, 'all');
    } else if (markerType) {
      // Toggling a marker-type checkbox. When 'all' equipment is active, apply
      // it to EVERY equipment (each has its own cluster/polyline), not just the
      // 'all' aggregate -- otherwise other units' clustered stops/trails stayed
      // on the map. When one unit is focused, toggle just that unit.
      const visible = vm.markerCheckboxes[markerType];
      if (newActive === 'all') {
        _.forEach(vm.equipmentStates, (__, id) => {
          loopAndToggleMarkerVisibility(id, markerType, visible);
        });
      } else {
        loopAndToggleMarkerVisibility(newActive, markerType, visible);
      }
    }
  }

  function loopAndToggleMarkerVisibility(equipmentId, markerType = 'all', visible) {
    const state = vm.equipmentStates[equipmentId];
    const visibleWasProvided = _.isBoolean(visible);
    _.forEach(state[`${markerType}Markers`], (marker) => {
      // Keyed by event name: marker.type is a display label ('Track', 'S.O.S.'),
      // which never matches a checkbox key, so this always fell through to the
      // `true` default and the stored checkbox state was never restored.
      const checkboxVal = _.get(vm.markerCheckboxes, marker.eventName || marker.type, true);
      const show = visibleWasProvided ? visible : checkboxVal;

      marker.setVisible(show);
    });
    // Base pins follow the same rule via their tagged eventName, but live in a
    // separate array (never the breadcrumb/cluster ones). 'all' toggles every
    // base pin (used when focusing an equipment); a specific type toggles only
    // the pins it governs (so unchecking Earthquake hides an earthquake pin).
    _.forEach(state.baseMarkers, (marker) => {
      if (markerType === 'all' || marker.eventName === markerType) {
        const checkboxVal = _.get(vm.markerCheckboxes, marker.eventName, true);
        marker.setVisible(visibleWasProvided ? visible : checkboxVal);
      }
    });
    if (markerType === 'all' || markerType === gpsTrack) {
      const addOrMakeVisible = markerType === 'all' && visibleWasProvided ?
        visible : vm.markerCheckboxes[gpsTrack];
      // this is necessary to make the cluster images disappear with markers
      repaintTheClusters(equipmentId, addOrMakeVisible);
      const polyLines = _.isArray(state.polyLine) ? state.polyLine : [state.polyLine];
      _.forEach(polyLines, (line) => {
        if (line) {
          line.setVisible(addOrMakeVisible);
        }
      });
    }
  }

  function repaintTheClusters(stateId, add) {
    // The cluster is created when the map's tiles finish loading, so a visibility
    // toggle that arrives before that (or for a state with no cluster) has nothing
    // to repaint -- guard rather than dereference undefined.
    if (!markerClusters[stateId]) {
      return;
    }
    // Rebuild from scratch every time: clear the cluster (removes its rendered
    // "N" icons and any stale/duplicate markers), then re-add the current stops
    // only when showing. This is idempotent -- repainting twice can't double the
    // badges -- and self-heals arrays that diverged from the cluster (e.g. after
    // a history-window change re-seeded the trail).
    markerClusters[stateId].clearMarkers();
    if (add) {
      markerClusters[stateId].addMarkers(vm.equipmentStates[stateId][gpsTrackMark]);
    }
  }

  function close() {
    MapService.removeIntervalsAndCloseInfoWins(infoBoxes);
  }

  // Copies text (e.g. an infobox's coordinates) to the clipboard and briefly
  // swaps the clicked copy icon to a check for feedback.
  // Copies the whole infobox -- name, type, date/time, address, coordinates --
  // read from the box's own text so it always matches what is shown. Returns the
  // copied text (handy for tests) and briefly swaps the icon to a check.
  function copyToClipboard(event) {
    const icon = _.get(event, 'currentTarget');
    const box = icon && icon.closest ? icon.closest('.panel') : null;
    const lines = box ? _.filter(_.map(box.querySelectorAll('h3, p'), el => el.textContent.trim())) : [];
    // De-duplicate: the header line repeats the name and date/time that also
    // appear as their own lines, so drop any line fully contained in a longer
    // line, then any exact duplicate.
    const text = _.uniq(_.filter(lines, (line, i) =>
      !_.some(lines, (other, idx) => idx !== i && other.length > line.length && other.includes(line))
    )).join('\n');
    if (text && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(_.noop);
    }
    if (icon) {
      icon.classList.remove('fa-copy');
      icon.classList.add('fa-check');
      setTimeout(() => {
        icon.classList.remove('fa-check');
        icon.classList.add('fa-copy');
      }, COPY_FEEDBACK_MS);
    }
    return text;
  }

  function closeNotificationPopup() {
    thePopup.css({
      opacity: 0,
      pointerEvents: 'none'
    });
  }

  function setupEventListeners() {

    /* New Event Listener */
    _.forEach(vm.panel.equipment, (equipmentId) => {
      $scope.$on(`event:${equipmentId}`, handleEquipmentEvent);
    });

    ObserverService.subscribe('pinpoint', vm.panelId, receivePinpointEvent);
  }

  function receivePinpointEvent({ event, options = {} }) {
    close();
    const eventName = event.eventName;
    if (pinpointTimeout) {
      clearTimeout(pinpointTimeout);
      pinpointMarker.setMap(null);
    }
    if (vm.equipmentStates[event.equipment.id]) {
      vm.useEventsToChangePosition(event.equipment, false);
      if (_.get(vm.markerCheckboxes, eventName, null) === false) {
        vm.toggleCheckbox(eventName);
      } else if (vm.markerCheckboxes.event === false) {
        vm.toggleCheckbox('event');
      }
      let marker = _.find(markers, ({ id, eventId }) => id === event.id || eventId === event.id);
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(new google.maps.LatLng(Number(event.data.latitude), Number(event.data.longitude)));
      if (marker) {
        if (previousTargetMarker) {
          previousTargetMarker.setZIndex(100);
        }
        marker.setZIndex(overNineThousand);
        previousTargetMarker = marker;
      } else {
        handleEquipmentEvent(null, event, 'true');
        marker = _.find(markers, { id: event.id });
        if (!marker) {
          marker = addNonTrackEventMarker(event.equipment.id, event, true);
        }
        marker.setAnimation(null);
      }

      handlePinpointMarker(event, pinpointLat, pinpointLng);

      if (angular.isDefined(googleEquipmentMap)) {
        google.maps.event.addListenerOnce(googleEquipmentMap, 'bounds_changed', () => {
          if (googleEquipmentMap.getZoom()) {
            googleEquipmentMap.setZoom(pinpointZoom || options.zoom || DEFAULT_PINPOINT_ZOOM);
          }
        });
        googleEquipmentMap.fitBounds(bounds);
      }
    }
  }

  function handlePinpointMarker(event) {
    if (pinpointMarker) {
      clearTimeout(pinpointTimeout);
      pinpointMarker.setMap(null);
      pinpointMarker
        .setPosition(new google.maps.LatLng(Number(event.data.latitude), Number(event.data.longitude)));
      pinpointMarker.setMap(googleEquipmentMap);
    } else {
      pinpointMarker = new MapService.Marker(
        googleEquipmentMap,
        {
          settings: {
            latitude: Number(event.data.latitude),
            longitude: Number(event.data.longitude)
          }
        },
      );
      pinpointMarker[symEquipment] = event.equipment;
    }
    pinpointMarker.setAnimation(google.maps.Animation.BOUNCE);
    pinpointTimeout = setTimeout(() => {
      pinpointMarker.setMap(null);
    }, animationLength.pop);
  }

  function eventIsATrack(ev) {
    return _.includes(ev.eventName.toUpperCase(), 'TRACK');
  }

  // TODO: turn color coding into configurable property
  function colorBank() {
    const colors = {};

    return function innerFunc() {
      /* eslint-disable no-magic-numbers */
      const color = `#${
        Math.round(0x1_00_00_00 + (0xff_ff_ff * Math.random()))
          .toString(16)
          .slice(1)}`;
      /* eslint-enable no-magic-numbers */
      // `colors.color` is a literal property that is never assigned -- the
      // writer below sets colors[<hex>] -- so this guard never fired and
      // duplicate trail colors were possible.
      if (colors[color]) {
        return innerFunc();
      }
      colors[color] = 1;
      return color;
    };
  }
}
